import {
  Activity,
  AlertTriangle,
  Bot,
  ChartNoAxesCombined,
  CircleUserRound,
  Home,
  LoaderCircle,
  LogOut,
  RefreshCw,
  Settings2,
  Unplug,
} from "lucide-react";
import { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";

import { useAuth } from "../hooks/use-auth";
import { useDashboard } from "../hooks/use-dashboard";
import { formatRelative } from "../lib/format";
import { cn } from "../lib/utils";
import { Brand } from "./brand";
import { EmergencyControl } from "./confirmations";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";

const navItems = [
  { to: "/", label: "Home", icon: Home },
  { to: "/trade", label: "Live Trade", mobileLabel: "Trade", icon: ChartNoAxesCombined },
  { to: "/activity", label: "Activity", icon: Activity },
  { to: "/strategy", label: "Strategy", icon: Settings2 },
  { to: "/connection", label: "Kraken Connection", mobileLabel: "Kraken", icon: Unplug },
];

const pageNames: Record<string, string> = {
  "/": "Home",
  "/trade": "Live Trade",
  "/activity": "Activity",
  "/strategy": "Strategy",
  "/connection": "Kraken Connection",
};

export function AppShell() {
  const { pathname } = useLocation();
  const { onLogout } = useAuth();
  const { dashboard, refreshing, error, refresh } = useDashboard();
  const pageName = pageNames[pathname] ?? "Hyper Profits";
  const connected = dashboard?.connection.connected ?? false;

  return (
    <div className="min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 hidden w-[236px] flex-col border-r border-white/8 bg-rail px-4 py-5 text-white md:flex">
        <Brand inverted className="px-1" />
        <nav className="mt-9 grid gap-1" aria-label="Primary navigation">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              className={({ isActive }) => cn(
                "group flex min-h-11 items-center gap-3 rounded-xl px-3 text-sm font-semibold text-rail-muted transition-colors hover:bg-white/7 hover:text-white",
                isActive && "bg-white/9 text-white",
              )}
            >
              {({ isActive }) => (
                <>
                  <span className={cn("grid size-8 place-items-center rounded-lg", isActive && "bg-signal text-signal-foreground")}>
                    <Icon className="size-4" aria-hidden="true" />
                  </span>
                  <span>{label}</span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto grid gap-4">
          <div className="rounded-2xl border border-white/9 bg-white/[0.045] p-4">
            <div className="flex items-center gap-2 text-xs font-semibold text-white">
              <span className={cn("size-2 rounded-full", connected ? "bg-signal" : "bg-coral")} />
              {connected ? "Kraken connected" : "Kraken disconnected"}
            </div>
            <p className="mt-2 text-[11px] leading-5 text-rail-muted">
              {dashboard ? `Checked ${formatRelative(dashboard.connection.lastCheckedAt)}` : "Waiting for account snapshot"}
            </p>
          </div>
          <Button variant="rail" size="sm" onClick={() => void onLogout()}>
            <LogOut data-icon="inline-start" />
            Sign out
          </Button>
        </div>
      </aside>

      <div className="min-h-screen md:pl-[236px]">
        <header className="sticky top-0 z-20 flex min-h-17 items-center justify-between gap-2 border-b border-border bg-background/95 px-4 py-2 backdrop-blur-sm sm:px-6 lg:px-10">
          <div className="flex min-w-0 items-center gap-3">
            <div className="md:hidden"><Brand compact /></div>
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-foreground">{pageName}</p>
              <p className="flex items-center gap-1.5 truncate text-[11px] text-muted-foreground">
                {refreshing && <RefreshCw className="size-3 animate-spin" aria-hidden="true" />}
                {refreshing ? "Refreshing account snapshot" : "ETH/USD · fee-aware automation"}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Badge variant={dashboard?.bot.mode === "live" ? "outline" : "secondary"} className="hidden min-[390px]:inline-flex">
              {dashboard?.bot.mode === "live" ? "Live" : "Paper"}
            </Badge>
            <Badge variant={dashboard?.bot.status === "running" ? "signal" : "outline"} className="hidden sm:inline-flex">
              <Bot className="size-3" aria-hidden="true" />
              {dashboard?.bot.status ?? "Loading"}
            </Badge>
            <EmergencyControl size="icon" responsiveLabel className="lg:w-auto lg:px-3" />
            <MobileAccountControl
              connected={connected}
              lastCheckedAt={dashboard?.connection.lastCheckedAt ?? null}
              onLogout={onLogout}
            />
          </div>
        </header>

        {error && dashboard && (
          <div className="border-b border-coral/25 bg-coral-soft px-4 py-3 text-destructive sm:px-6 lg:px-10" role="status" aria-live="polite">
            <div className="mx-auto flex max-w-[1300px] flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="flex items-start gap-2 text-sm leading-5">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                <span>Refresh failed. Showing the snapshot last updated {formatRelative(dashboard.bot.updatedAt)}.</span>
              </p>
              <Button variant="outline" size="sm" onClick={() => void refresh()}>
                <RefreshCw data-icon="inline-start" /> Retry
              </Button>
            </div>
          </div>
        )}

        <main className="mx-auto w-full max-w-[1380px] px-4 pb-28 pt-6 sm:px-6 sm:pt-8 md:pb-10 lg:px-10 lg:pt-10">
          <Outlet />
        </main>
      </div>

      <nav className="fixed inset-x-0 bottom-0 z-20 border-t border-white/8 bg-rail px-1 pb-[env(safe-area-inset-bottom)] text-white md:hidden" aria-label="Mobile navigation">
        <div className="mx-auto grid max-w-lg grid-cols-5">
          {navItems.map(({ to, label, mobileLabel, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              aria-label={label}
              className={({ isActive }) => cn(
                "flex min-h-16 flex-col items-center justify-center gap-1 rounded-lg px-1 text-[10px] font-semibold text-rail-muted transition-colors",
                isActive && "text-white",
              )}
            >
              {({ isActive }) => (
                <>
                  <span className={cn("grid size-7 place-items-center rounded-lg", isActive && "bg-signal text-signal-foreground")}>
                    <Icon className="size-4" aria-hidden="true" />
                  </span>
                  <span className="truncate">{mobileLabel ?? label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

function MobileAccountControl({
  connected,
  lastCheckedAt,
  onLogout,
}: {
  connected: boolean;
  lastCheckedAt: string | null;
  onLogout: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  async function signOut() {
    setSigningOut(true);
    try {
      await onLogout();
      setOpen(false);
    } finally {
      setSigningOut(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" className="md:hidden" aria-label="Open account and sign-out options">
          <CircleUserRound aria-hidden="true" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <span className="mb-1 grid size-10 place-items-center rounded-full bg-muted text-foreground">
            <CircleUserRound className="size-4" aria-hidden="true" />
          </span>
          <DialogTitle>Owner account</DialogTitle>
          <DialogDescription>
            You are signed in to the private Hyper Profits control room.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-xl border border-border bg-background/65 p-4">
          <p className="flex items-center gap-2 text-sm font-semibold">
            <span className={cn("size-2 rounded-full", connected ? "bg-positive" : "bg-destructive")} aria-hidden="true" />
            {connected ? "Kraken connected" : "Kraken disconnected"}
          </p>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            {lastCheckedAt ? `Last checked ${formatRelative(lastCheckedAt)}` : "No account check is available yet."}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" asChild>
            <Link to="/connection" onClick={() => setOpen(false)}>Kraken connection</Link>
          </Button>
          <Button variant="secondary" onClick={() => void signOut()} disabled={signingOut}>
            {signingOut ? <LoaderCircle data-icon="inline-start" className="animate-spin" /> : <LogOut data-icon="inline-start" />}
            Sign out
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
